import React, { useEffect, useRef } from "react";
import { NavLink, Navigate, Outlet, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NoAccess } from "@/components/ProtectedRoute";

export type HubTab = { to: string; label: string; icon: React.ComponentType<any>; perm: string; description: string; end?: boolean; testid: string; group?: string };

// Sección del menú que agrupa varias pantallas en pestañas (p. ej. Puntos, Gestión de clientes).
// Cada pestaña es una ruta propia: se puede enlazar, recargar y volver con "Atrás".
export function SectionHub({ title, base, tabs, testid }: { title: string; base: string; tabs: HubTab[]; testid: string }) {
  const { user } = useAuth();
  const location = useLocation();
  const perms = user?.permissions || [];
  const tabsRef = useRef<HTMLElement>(null);
  // En pantallas angostas las pestañas se desplazan: mantener visible la activa.
  useEffect(() => {
    tabsRef.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [location.pathname]);
  const allowed = tabs.filter((t) => perms.includes(t.perm));
  if (!allowed.length) return <NoAccess section />;

  const active = tabs.find((t) => matchPath({ path: t.to, end: true }, location.pathname));
  // Entrar a la sección sin permiso para su primera pestaña: ir a la primera permitida.
  if (location.pathname === base && active && !perms.includes(active.perm)) return <Navigate to={allowed[0].to} replace />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid={testid}>
      <div>
        <h1 className="text-3xl font-serif font-medium">{title}</h1>
        {active && <p className="text-sm text-slate-600 mt-1 max-w-3xl" data-testid={`${testid}-description`}>{active.description}</p>}
      </div>
      <nav ref={tabsRef} aria-label={`Opciones de ${title}`} className="border-b border-slate-200 -mx-1 overflow-x-auto">
        <ul className="flex gap-1 px-1 min-w-max">
          {allowed.map((t, i) => (
            <li key={t.to} className="flex items-end">
              {t.group && t.group !== allowed[i - 1]?.group && (
                <span className={`self-center text-[11px] uppercase tracking-wider font-semibold text-slate-500 pr-2 ${i ? "pl-3 ml-2 border-l border-slate-200" : "pl-1"}`} aria-hidden="true">{t.group}</span>
              )}
              <NavLink
                to={t.to}
                end={t.end}
                data-testid={t.testid}
                aria-label={t.group ? `${t.label} · ${t.group}` : undefined}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 ${t.group ? "px-3" : "px-4"} py-3 text-sm border-b-2 -mb-px transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-t-md ${
                    isActive ? "border-navy text-navy font-semibold" : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                  }`
                }
              >
                <t.icon className="w-4 h-4" aria-hidden="true" /> {t.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <Outlet />
    </div>
  );
}
